import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    AlertTriangle,
    Boxes,
    Car,
    Factory,
    Layers,
    Package,
    PackageOpen,
    Plus,
    Search,
    Sliders,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

import { useAuth } from "@/contexts/AuthContext";
import { PartAPI, StockAPI, PurchaseOrderAPI, SupplierAPI } from "@/lib/api";

// ---------- helpers
const inr = (minor) => `₹${(Math.round((minor || 0) / 100)).toLocaleString("en-IN")}`;
const severityBadge = (sev) =>
    sev === "critical"
        ? "border-red-400 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300"
        : sev === "low"
        ? "border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-300"
        : "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200";

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" });
    } catch {
        return iso;
    }
};

const MOVEMENT_LABEL = {
    receipt: "Receipt", consumption: "Consumed", return: "Return",
    adjustment_positive: "Adj +", adjustment_negative: "Adj −",
    reservation: "Reserved", reservation_release: "Released",
    transfer_out: "Transfer out", transfer_in: "Transfer in",
};

// ---------- Tab: Parts catalogue
function PartsTab({ branchId }) {
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await PartAPI.list({ q: q || undefined, branch_id: branchId, page_size: 200 });
            setItems(d.items || []);
        } catch (e) {
            toast.error("Failed to load parts");
        } finally {
            setLoading(false);
        }
    }, [q, branchId]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4" data-testid="inventory-tab-parts-pane">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                    <Input
                        data-testid="parts-search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by part number, name or barcode…"
                        className="pl-9"
                    />
                </div>
                <span className="font-mono text-xs text-zinc-500" data-testid="parts-total-count">
                    {loading ? "…" : `${items.length} parts`}
                </span>
            </div>

            <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                <Table data-testid="parts-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest">Part #</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest">Name</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest">Category</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest">Cost</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest text-right">On-hand</TableHead>
                            <TableHead className="font-mono text-[10px] uppercase tracking-widest text-right">Min</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((p) => {
                            const bs = p.branch_stock || { quantity_on_hand: 0, effective_min_stock: p.min_stock };
                            const sev = bs.quantity_on_hand === 0
                                ? "critical"
                                : bs.quantity_on_hand <= bs.effective_min_stock
                                ? "low"
                                : "ok";
                            return (
                                <TableRow key={p.id} data-testid={`part-row-${p.id}`}>
                                    <TableCell className="font-mono">{p.part_number}</TableCell>
                                    <TableCell className="font-medium">{p.name}</TableCell>
                                    <TableCell><Badge variant="outline" className="font-mono text-[10px]">{p.category}</Badge></TableCell>
                                    <TableCell className="font-mono">{inr(p.unit_cost_minor)}</TableCell>
                                    <TableCell className="text-right" data-testid={`part-stock-cell-${p.id}`}>
                                        <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium ${severityBadge(sev)}`}>
                                            {bs.quantity_on_hand} {p.unit_of_measure}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right font-mono text-xs text-zinc-500">{p.min_stock}</TableCell>
                                </TableRow>
                            );
                        })}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-zinc-500">No parts.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

// ---------- Tab: Stock by Branch (with Adjust dialog)
function StockByBranchTab({ branchId, onChanged }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [lowOnly, setLowOnly] = useState(false);
    const [adjustOpen, setAdjustOpen] = useState(false);
    const [adjustTarget, setAdjustTarget] = useState(null);

    const load = useCallback(async () => {
        if (!branchId) return;
        setLoading(true);
        try {
            const d = await StockAPI.branchStock(branchId, { q: q || undefined, low_only: lowOnly, page_size: 500 });
            setItems(d.items || []);
        } catch {
            toast.error("Failed to load branch stock");
        } finally { setLoading(false); }
    }, [branchId, q, lowOnly]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4" data-testid="inventory-tab-stock-pane">
            <div className="flex flex-wrap items-center gap-2">
                <Input
                    data-testid="stock-search-input"
                    value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Search parts at this branch…"
                    className="flex-1 min-w-[200px]"
                />
                <Button
                    variant={lowOnly ? "default" : "outline"}
                    onClick={() => setLowOnly(!lowOnly)}
                    data-testid="stock-low-only-toggle"
                >Low only</Button>
            </div>
            <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                <Table data-testid="stock-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Part</TableHead>
                            <TableHead className="text-right">On-hand</TableHead>
                            <TableHead className="text-right">Min</TableHead>
                            <TableHead className="text-right">Avg cost</TableHead>
                            <TableHead>Severity</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((s) => (
                            <TableRow key={s.part_id} data-testid={`stock-row-${s.part_id}`}>
                                <TableCell>
                                    <div className="font-mono text-xs text-zinc-500">{s.part_number}</div>
                                    <div className="font-medium">{s.name}</div>
                                </TableCell>
                                <TableCell className="text-right font-mono" data-testid={`stock-onhand-${s.part_id}`}>
                                    {s.quantity_on_hand} {s.unit_of_measure}
                                </TableCell>
                                <TableCell className="text-right font-mono">{s.effective_min_stock}</TableCell>
                                <TableCell className="text-right font-mono text-xs">{inr(s.avg_cost_minor)}</TableCell>
                                <TableCell>
                                    <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${severityBadge(s.severity)}`}>
                                        {s.severity}
                                    </span>
                                </TableCell>
                                <TableCell className="text-right">
                                    <Button
                                        size="sm" variant="outline"
                                        data-testid={`part-adjust-btn-${s.part_id}`}
                                        onClick={() => { setAdjustTarget(s); setAdjustOpen(true); }}
                                    ><Sliders className="mr-1 h-3 w-3" />Adjust</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-zinc-500">
                                {lowOnly ? "No low-stock items." : "No parts."}
                            </TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <AdjustDialog
                open={adjustOpen}
                onOpenChange={setAdjustOpen}
                target={adjustTarget}
                branchId={branchId}
                onDone={() => { load(); onChanged && onChanged(); }}
            />
        </div>
    );
}

function AdjustDialog({ open, onOpenChange, target, branchId, onDone }) {
    const [delta, setDelta] = useState("1");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => { if (open) { setDelta("1"); setReason(""); } }, [open]);

    const submit = async () => {
        const n = Number(delta);
        if (!n || !Number.isInteger(n)) { toast.error("Delta must be a non-zero integer"); return; }
        if (!reason.trim()) { toast.error("Reason is required"); return; }
        setSubmitting(true);
        try {
            await PartAPI.adjust(target.part_id, { branch_id: branchId, delta: n, reason: reason.trim() });
            toast.success(`Adjusted ${n > 0 ? "+" : ""}${n} on ${target.part_number}`);
            onOpenChange(false);
            onDone && onDone();
        } catch (e) {
            const code = e?.response?.data?.error?.code;
            const msg = e?.response?.data?.error?.message || "Adjustment failed";
            toast.error(code ? `${code}: ${msg}` : msg);
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="adjust-dialog">
                <DialogHeader>
                    <DialogTitle>Adjust stock — {target?.part_number}</DialogTitle>
                    <DialogDescription>
                        Writes a stock movement and updates on-hand atomically. A reason is mandatory and lands in the audit log.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="delta">Delta (negative to remove)</Label>
                        <Input
                            id="delta" type="number" value={delta}
                            onChange={(e) => setDelta(e.target.value)}
                            data-testid="adjust-delta-input"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="reason">Reason *</Label>
                        <Textarea
                            id="reason" value={reason} onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Damaged in stockroom"
                            data-testid="adjust-reason-input"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="adjust-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={submitting} data-testid="adjust-submit">
                        {submitting ? "Submitting…" : "Submit"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Tab: Movements ledger
function MovementsTab({ branchId }) {
    const [parts, setParts] = useState([]);
    const [selPartId, setSelPartId] = useState("");
    const [items, setItems] = useState([]);
    const [type, setType] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        PartAPI.list({ branch_id: branchId, page_size: 500 }).then((d) => setParts(d.items || []));
    }, [branchId]);

    useEffect(() => {
        if (!selPartId) { setItems([]); return; }
        setLoading(true);
        PartAPI.movements(selPartId, { branch_id: branchId, movement_type: type || undefined, page_size: 200 })
            .then((d) => setItems(d.items || []))
            .finally(() => setLoading(false));
    }, [selPartId, branchId, type]);

    return (
        <div className="space-y-4" data-testid="inventory-tab-movements-pane">
            <div className="flex flex-wrap items-center gap-2">
                <Select value={selPartId} onValueChange={setSelPartId}>
                    <SelectTrigger className="w-[320px]" data-testid="movements-part-select">
                        <SelectValue placeholder="Select a part…" />
                    </SelectTrigger>
                    <SelectContent>
                        {parts.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                                <span className="font-mono text-xs text-zinc-500">{p.part_number}</span>&nbsp;{p.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={type} onValueChange={(v) => setType(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[200px]" data-testid="movements-type-select">
                        <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All types</SelectItem>
                        {Object.entries(MOVEMENT_LABEL).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                <Table data-testid="movements-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>When</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Balance after</TableHead>
                            <TableHead>Reference</TableHead>
                            <TableHead>Reason</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {!selPartId && (
                            <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-zinc-500">
                                Select a part to view its movement ledger.
                            </TableCell></TableRow>
                        )}
                        {items.map((m) => (
                            <TableRow key={m.id}>
                                <TableCell className="font-mono text-xs">{fmtDateTime(m.created_at)}</TableCell>
                                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{MOVEMENT_LABEL[m.movement_type] || m.movement_type}</Badge></TableCell>
                                <TableCell className="text-right font-mono">{m.quantity}</TableCell>
                                <TableCell className="text-right font-mono font-semibold">{m.running_balance_after}</TableCell>
                                <TableCell className="text-xs font-mono text-zinc-500">
                                    {m.reference?.type && (
                                        <>
                                            <span>{m.reference.type}</span>
                                            {m.reference.po_id && (
                                                <Link to={`/purchase-orders/${m.reference.po_id}`} className="ml-1 text-brand-redDeep underline-offset-2 hover:underline">PO →</Link>
                                            )}
                                            {m.reference.type === "job_card" && m.reference.id && (
                                                <Link to={`/jobs/${m.reference.id}`} className="ml-1 text-brand-redDeep underline-offset-2 hover:underline">Job →</Link>
                                            )}
                                        </>
                                    )}
                                </TableCell>
                                <TableCell className="max-w-[280px] truncate text-xs text-zinc-600 dark:text-zinc-400">{m.reason || "—"}</TableCell>
                            </TableRow>
                        ))}
                        {selPartId && !loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-zinc-500">No movements for this filter.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

// ---------- Tab: Low-Stock Alerts
function AlertsTab({ branchId }) {
    const [alerts, setAlerts] = useState([]);
    const [counts, setCounts] = useState({ critical: 0, low: 0 });

    const load = useCallback(async () => {
        const d = await StockAPI.lowStockAlerts({ branch_id: branchId });
        setAlerts(d.items || []);
        setCounts({ critical: d.critical || 0, low: d.low || 0 });
    }, [branchId]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4" data-testid="inventory-tab-alerts-pane">
            <div className="flex gap-2">
                <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-mono ${severityBadge("critical")}`} data-testid="alerts-critical-count">
                    <AlertTriangle className="h-3 w-3" /> {counts.critical} critical
                </span>
                <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-mono ${severityBadge("low")}`} data-testid="alerts-low-count">
                    {counts.low} low
                </span>
            </div>
            <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                <Table data-testid="low-stock-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Part</TableHead>
                            <TableHead>Branch</TableHead>
                            <TableHead className="text-right">On-hand</TableHead>
                            <TableHead className="text-right">Min</TableHead>
                            <TableHead>Severity</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {alerts.map((a) => (
                            <TableRow key={`${a.part_id}-${a.branch_id}`} data-testid={`low-stock-row-${a.part_id}`}>
                                <TableCell>
                                    <div className="font-mono text-xs text-zinc-500">{a.part_number}</div>
                                    <div className="font-medium">{a.name}</div>
                                </TableCell>
                                <TableCell className="font-mono text-xs">{a.branch_code}</TableCell>
                                <TableCell className="text-right font-mono">{a.quantity_on_hand} {a.unit_of_measure}</TableCell>
                                <TableCell className="text-right font-mono">{a.effective_min_stock}</TableCell>
                                <TableCell>
                                    <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${severityBadge(a.severity)}`} data-testid={`low-stock-severity-${a.severity}`}>
                                        {a.severity}
                                    </span>
                                </TableCell>
                                <TableCell className="text-right">
                                    <Link to={`/purchase-orders/new?supplier_hint=${(a.supplier_ids || [])[0] || ""}&branch_id=${a.branch_id}&part_id=${a.part_id}`}>
                                        <Button size="sm" variant="outline" data-testid={`low-stock-raise-po-${a.part_id}`}>
                                            <Plus className="mr-1 h-3 w-3" />Raise PO
                                        </Button>
                                    </Link>
                                </TableCell>
                            </TableRow>
                        ))}
                        {alerts.length === 0 && (
                            <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-zinc-500">No low-stock alerts — every part is comfortably above its threshold.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

// ---------- Tab: Stock by Brand (vehicle make/model + manufacturer)
function StockByBrandTab({ branchId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [make, setMake] = useState("");
    const [manufacturer, setManufacturer] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await PartAPI.list({ branch_id: branchId, page_size: 500 });
            setItems(d.items || []);
        } catch {
            toast.error("Failed to load parts");
        } finally {
            setLoading(false);
        }
    }, [branchId]);

    useEffect(() => { load(); }, [load]);

    const makeOf = (p) => (p.vehicle_make && p.vehicle_make.trim()) || "Universal";

    const makeOptions = useMemo(
        () => Array.from(new Set(items.map(makeOf))).sort((a, b) =>
            a === "Universal" ? 1 : b === "Universal" ? -1 : a.localeCompare(b)),
        [items]
    );
    const mfrOptions = useMemo(
        () => Array.from(new Set(items.map((p) => p.manufacturer).filter(Boolean))).sort(),
        [items]
    );

    const groups = useMemo(() => {
        const term = q.trim().toLowerCase();
        const filtered = items.filter((p) => {
            if (make && makeOf(p) !== make) return false;
            if (manufacturer && p.manufacturer !== manufacturer) return false;
            if (!term) return true;
            const hay = [p.part_number, p.name, p.manufacturer, ...(p.vehicle_models || [])]
                .filter(Boolean).join(" ").toLowerCase();
            return hay.includes(term);
        });
        const map = {};
        for (const p of filtered) (map[makeOf(p)] ||= []).push(p);
        return Object.keys(map)
            .sort((a, b) => (a === "Universal" ? 1 : b === "Universal" ? -1 : a.localeCompare(b)))
            .map((m) => {
                const parts = map[m].slice().sort((x, y) => x.name.localeCompare(y.name));
                const totalOnHand = parts.reduce((s, p) => s + ((p.branch_stock || {}).quantity_on_hand || 0), 0);
                return { make: m, parts, totalOnHand };
            });
    }, [items, q, make, manufacturer]);

    return (
        <div className="space-y-4" data-testid="inventory-tab-brand-pane">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                    <Input
                        data-testid="brand-search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search part, model or manufacturer…"
                        className="pl-9"
                    />
                </div>
                <Select value={make || "_all"} onValueChange={(v) => setMake(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[200px]" data-testid="brand-make-filter">
                        <Car className="mr-1.5 h-3.5 w-3.5 text-zinc-400" />
                        <SelectValue placeholder="All makes" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All makes</SelectItem>
                        {makeOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={manufacturer || "_all"} onValueChange={(v) => setManufacturer(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[210px]" data-testid="brand-manufacturer-filter">
                        <Factory className="mr-1.5 h-3.5 w-3.5 text-zinc-400" />
                        <SelectValue placeholder="All manufacturers" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All manufacturers</SelectItem>
                        {mfrOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {!loading && groups.length === 0 && (
                <Card><CardContent className="py-12 text-center text-sm text-zinc-500">No parts match these filters.</CardContent></Card>
            )}

            {groups.map((g) => (
                <Card key={g.make} data-testid={`brand-group-${g.make}`}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-zinc-100 py-3 dark:border-zinc-800">
                        <CardTitle className="flex items-center gap-2 text-base">
                            {g.make === "Universal"
                                ? <Boxes className="h-4 w-4 text-brand-blue" />
                                : <Car className="h-4 w-4 text-brand-red" />}
                            <span className="font-display font-bold">{g.make}</span>
                            {g.make === "Universal" && <span className="text-xs font-normal text-zinc-400">fits all vehicles</span>}
                        </CardTitle>
                        <div className="flex items-center gap-3 font-mono text-xs text-zinc-500">
                            <span data-testid={`brand-group-count-${g.make}`}>{g.parts.length} parts</span>
                            <span className="hidden sm:inline">·</span>
                            <span className="hidden sm:inline">{g.totalOnHand} on-hand</span>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table data-testid={`brand-table-${g.make}`}>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="font-mono text-[10px] uppercase tracking-widest">Part</TableHead>
                                    <TableHead className="font-mono text-[10px] uppercase tracking-widest">Model(s)</TableHead>
                                    <TableHead className="font-mono text-[10px] uppercase tracking-widest">Manufacturer</TableHead>
                                    <TableHead className="font-mono text-[10px] uppercase tracking-widest">Category</TableHead>
                                    <TableHead className="font-mono text-[10px] uppercase tracking-widest text-right">Cost</TableHead>
                                    <TableHead className="font-mono text-[10px] uppercase tracking-widest text-right">On-hand</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {g.parts.map((p) => {
                                    const bs = p.branch_stock || { quantity_on_hand: 0, effective_min_stock: p.min_stock };
                                    const sev = bs.quantity_on_hand === 0
                                        ? "critical"
                                        : bs.quantity_on_hand <= bs.effective_min_stock
                                        ? "low"
                                        : "ok";
                                    return (
                                        <TableRow key={p.id} data-testid={`brand-part-row-${p.id}`}>
                                            <TableCell>
                                                <div className="font-mono text-xs text-zinc-500">{p.part_number}</div>
                                                <div className="font-medium">{p.name}</div>
                                            </TableCell>
                                            <TableCell>
                                                {(p.vehicle_models || []).length ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {p.vehicle_models.map((m) => (
                                                            <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>
                                                        ))}
                                                    </div>
                                                ) : <span className="text-xs text-zinc-400">—</span>}
                                            </TableCell>
                                            <TableCell className="text-sm">{p.manufacturer || "—"}</TableCell>
                                            <TableCell><Badge variant="outline" className="font-mono text-[10px]">{p.category}</Badge></TableCell>
                                            <TableCell className="text-right font-mono text-xs">{inr(p.unit_cost_minor)}</TableCell>
                                            <TableCell className="text-right" data-testid={`brand-stock-cell-${p.id}`}>
                                                <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${severityBadge(sev)}`}>
                                                    {bs.quantity_on_hand} {p.unit_of_measure}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}

// ---------- Page shell
export default function InventoryPage() {
    const { currentBranchId } = useAuth();
    const [params, setParams] = useSearchParams();
    const tab = params.get("tab") || "parts";

    const setTab = (v) => {
        const p = new URLSearchParams(params);
        p.set("tab", v);
        setParams(p, { replace: true });
    };

    return (
        <div className="space-y-6" data-testid="inventory-page">
            <header className="flex items-end justify-between gap-4">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Stock control</div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight">Inventory</h1>
                    <p className="mt-1 text-sm text-zinc-500">Parts catalogue · stock by vehicle brand &amp; model · stock-on-hand by branch · movement ledger · low-stock alerts.</p>
                </div>
                <Link to="/suppliers"><Button variant="outline" data-testid="link-to-suppliers"><Boxes className="mr-1.5 h-4 w-4" />Suppliers</Button></Link>
            </header>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="inventory-tabs">
                    <TabsTrigger value="parts" data-testid="inventory-tab-parts"><Package className="mr-1 h-3.5 w-3.5" />Parts</TabsTrigger>
                    <TabsTrigger value="brand" data-testid="inventory-tab-brand"><Car className="mr-1 h-3.5 w-3.5" />Stock by brand</TabsTrigger>
                    <TabsTrigger value="stock" data-testid="inventory-tab-stock"><Layers className="mr-1 h-3.5 w-3.5" />Stock by branch</TabsTrigger>
                    <TabsTrigger value="movements" data-testid="inventory-tab-movements"><PackageOpen className="mr-1 h-3.5 w-3.5" />Movements</TabsTrigger>
                    <TabsTrigger value="alerts" data-testid="inventory-tab-alerts"><AlertTriangle className="mr-1 h-3.5 w-3.5" />Low-stock alerts</TabsTrigger>
                </TabsList>
                <TabsContent value="parts" className="mt-4"><PartsTab branchId={currentBranchId} /></TabsContent>
                <TabsContent value="brand" className="mt-4"><StockByBrandTab branchId={currentBranchId} /></TabsContent>
                <TabsContent value="stock" className="mt-4"><StockByBranchTab branchId={currentBranchId} /></TabsContent>
                <TabsContent value="movements" className="mt-4"><MovementsTab branchId={currentBranchId} /></TabsContent>
                <TabsContent value="alerts" className="mt-4"><AlertsTab branchId={currentBranchId} /></TabsContent>
            </Tabs>
        </div>
    );
}
